import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Alert, Box, Button, Chip, CircularProgress, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard } from '../utils/clipboard';
import { DeveloperApiError } from './api';
import { ENVIRONMENTS, ERROR_CODES, OWN_WORDS, Tone } from './labels';
import type { ProductionKeyBannerItem } from './types';

const CHIP_COLOR: Record<Tone, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
    neutral: 'default',
};

/** A status chip. `neutral` is the outlined grey chip: a sandbox outcome is never shown as an error;
 *  `sandbox` adds the flask the console uses for the same outcomes (S4c); `icon` any other (an
 *  Integrations verdict: text, icon and tone, never colour alone). */
export const ToneChip = ({ label, tone, hint, sandbox = false, icon }: { label: string; tone: Tone; hint?: string; sandbox?: boolean; icon?: React.ReactElement }) => {
    const chip = (
        <Chip
            size="small"
            label={label}
            color={CHIP_COLOR[tone]}
            variant={tone === 'neutral' ? 'outlined' : 'filled'}
            icon={icon || (sandbox ? <ScienceOutlinedIcon sx={{ fontSize: 14 }} /> : undefined)}
        />
    );
    return hint ? <Tooltip title={hint}><span>{chip}</span></Tooltip> : chip;
};

export const EnvironmentChip = ({ environment }: { environment: string | null | undefined }) => {
    if (!environment) {
        return null;
    }
    const sandbox = environment === 'sandbox';
    return (
        <Chip
            size="small"
            data-testid="environment-chip"
            label={ENVIRONMENTS[environment] || environment}
            color={sandbox ? 'warning' : 'default'}
            variant="outlined"
            icon={sandbox ? <ScienceOutlinedIcon sx={{ fontSize: 16 }} /> : undefined}
        />
    );
};

export const CopyButton = ({ value, label }: { value: string | null | undefined; label: string }) => {
    const { enqueueSnackbar } = useSnackbar();
    if (!value) {
        return null;
    }
    return (
        <Tooltip title={`Copy ${label}`}>
            <IconButton
                size="small"
                aria-label={`copy ${label}`}
                onClick={(ev) => {
                    ev.stopPropagation();
                    copyTextToClipboard(value);
                    enqueueSnackbar(`${label[0].toUpperCase()}${label.slice(1)} copied`, { variant: 'success', autoHideDuration: 1000 });
                }}
            >
                <ContentCopyIcon fontSize="inherit" />
            </IconButton>
        </Tooltip>
    );
};

export const formatTs = (value: string | null | undefined) => (value ? moment(value).format('D MMM YYYY, HH:mm:ss') : '—');

export const Timestamp = ({ value }: { value: string | null | undefined }) => (
    value ? (
        <Tooltip title={value}>
            <span style={{ whiteSpace: 'nowrap' }}>{formatTs(value)}</span>
        </Tooltip>
    ) : <span>—</span>
);

export const mono = { fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' as const };

/** A stored body or payload: monospace, wrapped, scrolled when long. */
export const CodeBlock = ({ text, maxHeight = 320, testId }: { text: string | null | undefined; maxHeight?: number; testId?: string }) => (
    <Box
        component="pre"
        data-testid={testId}
        sx={{ ...mono, m: 0, p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, maxHeight, overflow: 'auto', whiteSpace: 'pre-wrap' }}
    >
        {text || ''}
    </Box>
);

/** The words for a refusal: the page's own for the contract's codes, else the server's message. */
export const describeError = (error: unknown) => {
    if (error instanceof DeveloperApiError) {
        if (error.code && error.code !== 'forbidden' && ERROR_CODES[error.code]) {
            return (!OWN_WORDS.has(error.code) && error.body?.message) || ERROR_CODES[error.code];
        }
        return error.message;
    }
    return 'Something went wrong. Try again.';
};

/** Per-entry reasons of a refusal (`errors`), whatever their shape. */
export const errorEntries = (error: unknown): string[] => {
    if (!(error instanceof DeveloperApiError)) {
        return [];
    }
    return error.errors.map((entry) => {
        const where = entry.field || entry.list || entry.kind;
        const what = entry.value || entry.entry;
        const why = entry.message || entry.reason || entry.error || entry.code;
        const parts = [where, what && `"${what}"`].filter(Boolean).join(' ');
        return why ? `${parts ? `${parts}: ` : ''}${why}` : JSON.stringify(entry);
    });
};

export const ErrorAlert = ({ error, onRetry }: { error: unknown; onRetry?: () => void }) => (
    <Alert
        severity="error"
        sx={{ my: 1 }}
        action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
    >
        {describeError(error)}
        {errorEntries(error).length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {errorEntries(error).map((line) => <li key={line}>{line}</li>)}
            </Box>
        )}
    </Alert>
);

export const LoadMore = ({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) => (
    hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <Button onClick={onClick} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : undefined}>
                Load more
            </Button>
        </Box>
    ) : null
);

export const Loading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
);

export const ProductionKeyBanner = ({ items }: { items: ProductionKeyBannerItem[] | null | undefined }) => (
    items && items.length > 0 ? (
        <Stack spacing={1} sx={{ my: 1 }} data-testid="production-key-banner">
            {items.map((item, idx) => (
                <Alert key={`${item.kind}-${item.restech}-${item.account}-${idx}`} severity="warning">{item.text}</Alert>
            ))}
        </Stack>
    ) : null
);

/** Every Developer page: its title, the tenant's environment and, while a vendor key is shared with
 *  production, the production-key banner (D3, O16: shown on both Developer sections). */
export const DeveloperShell = ({ title, actions, children }: { title: string; actions?: React.ReactNode; children: React.ReactNode }) => {
    const { environment, productionKeyBanner } = React.useContext(ContextStore) as any;
    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 }, maxWidth: 1400 }}>
            <PageMeta title={`Developer: ${title}`} />
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1, rowGap: 1 }}>
                <PageHeading>{title}</PageHeading>
                <EnvironmentChip environment={environment} />
                <Box sx={{ flexGrow: 1 }} />
                {actions}
            </Stack>
            <ProductionKeyBanner items={productionKeyBanner} />
            {children}
        </Box>
    );
};

export const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
        <Box sx={{ overflowWrap: 'anywhere' }}>{children}</Box>
    </Box>
);

/** A record's detail beside its list: full width on a phone. */
export const DetailDrawer = ({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: React.ReactNode; children: React.ReactNode }) => (
    <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        // Above the app's side menu (zIndex 10000 in layout/main.js).
        sx={{ zIndex: 10002 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640, md: 760 }, p: { xs: 2, sm: 3 } } }}
    >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>{title}</Box>
            <IconButton aria-label="close" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        {children}
    </Drawer>
);

/** "3 created · 1 skipped": the per-booking outcomes of a request, zeros left out. */
export const summaryText = (summary: Record<string, number> | null | undefined) => {
    if (!summary || !summary.bookings) {
        return null;
    }
    const kinds = ['created', 'updated', 'cancelled', 'unchanged', 'skipped', 'failed', 'pending'];
    return kinds.filter((k) => summary[k]).map((k) => `${summary[k]} ${k}`).join(' · ');
};

/** A horizontally scrolling table holder, so a wide table never widens the page on a phone. */
export const TableScroll = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>{children}</Box>
);
